import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { assetsAPI, tenantUsersAPI } from '../../../api';
import AssetForm from './AssetForm';
import Layout from '../../../components/Layout';
import { Tooltip, TooltipProvider } from '../../../components/ui/Tooltip';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Card, CardContent } from '../../../components/ui/Card';
import SlideOver from '../../../components/ui/SlideOver';
import { FormLabel } from '../../../components/ui/FormLabel';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../components/ui/Table';
import { Edit, Eye, Package } from 'lucide-react';
import {
  ASSET_CATEGORIES,
  ASSET_STATUSES,
  labelFor,
  formatAssetDate,
  dateInDays,
} from '../../../lib/assetConstants';

const selectClass =
  'flex h-9 w-full border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

const statusBadgeClass = (status) => {
  const map = {
    in_service: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
    assigned: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
    under_repair: 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300',
    lost: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
    stolen: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
  };
  return map[status] || 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
};

const AssetsList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [assignedFilter, setAssignedFilter] = useState('');
  const [tenantUsers, setTenantUsers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [showCreateSlideOver, setShowCreateSlideOver] = useState(false);
  const [showEditSlideOver, setShowEditSlideOver] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);

  const urlFilter = searchParams.get('filter') || '';

  useEffect(() => {
    tenantUsersAPI.list().then((res) => {
      const users = res?.users || res || [];
      setTenantUsers(Array.isArray(users) ? users : []);
    }).catch(console.error);
  }, []);

  useEffect(() => {
    if (urlFilter === 'calibrationDue') {
      setStatusFilter('');
    } else if (urlFilter === 'underRepair') {
      setStatusFilter('under_repair');
    }
  }, [urlFilter]);

  useEffect(() => {
    fetchAssets();
  }, [pagination.page, search, categoryFilter, statusFilter, assignedFilter, urlFilter]);

  const fetchAssets = async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: search.trim() || undefined,
        category: categoryFilter || undefined,
        status: statusFilter || undefined,
        assignedUserId: assignedFilter === '__none__' ? '__none__' : assignedFilter || undefined,
      };

      if (urlFilter === 'calibrationDue') {
        params.calibrationDueBefore = dateInDays(30);
      } else if (urlFilter === 'warrantyDue') {
        params.warrantyDueBefore = dateInDays(30);
      }

      const response = await assetsAPI.getAll(params);
      setAssets(response.assets || []);
      setPagination((prev) => ({
        ...prev,
        total: response.pagination?.total ?? 0,
        limit: response.pagination?.limit ?? prev.limit,
        page: response.pagination?.page ?? prev.page,
      }));
    } catch (err) {
      setError(err.message || 'Failed to load assets');
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(pagination.total / pagination.limit));

  const handleCreateSuccess = () => {
    setShowCreateSlideOver(false);
    fetchAssets();
  };

  const handleEditSuccess = () => {
    setShowEditSlideOver(false);
    setSelectedAsset(null);
    fetchAssets();
  };

  const filterBanner = () => {
    if (urlFilter === 'calibrationDue') {
      return 'Showing assets with calibration due within 30 days';
    }
    if (urlFilter === 'warrantyDue') {
      return 'Showing assets with warranty expiring within 30 days';
    }
    if (urlFilter === 'underRepair') {
      return 'Showing assets under repair';
    }
    return null;
  };

  const banner = filterBanner();

  return (
    <TooltipProvider>
      <Layout>
        <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="md:flex md:items-center md:justify-between">
              <div className="flex-1 min-w-0">
                <h1 className="text-display text-[var(--ds-text-primary)] font-bold">
                  Assets & Stock
                </h1>
                <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">
                  Business asset register — tools, test equipment, and plant
                </p>
              </div>
              <div className="mt-4 flex md:mt-0 md:ml-4 gap-3 flex-wrap">
                <Button variant="outline" size="sm" onClick={() => navigate('/tenant/dashboard')}>
                  ← Back to Dashboard
                </Button>
                <Button variant="outline" size="sm" onClick={() => navigate('/tenant/stock')}>
                  Stock
                </Button>
                <Button variant="outline" size="sm" onClick={() => navigate('/tenant/assets/import')}>
                  Import receipt
                </Button>
                <Button variant="primary" onClick={() => setShowCreateSlideOver(true)}>
                  + Add Asset
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {banner && (
            <div className="mb-4 px-4 py-3 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200 text-sm flex items-center justify-between">
              <span>{banner}</span>
              <Button variant="outline" size="sm" onClick={() => navigate('/tenant/assets')}>
                Clear filter
              </Button>
            </div>
          )}

          <Card className="mb-6 bg-white dark:bg-gray-800">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="md:col-span-2">
                  <FormLabel className="text-gray-700 dark:text-gray-300">Search</FormLabel>
                  <Input
                    type="text"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPagination((p) => ({ ...p, page: 1 }));
                    }}
                    placeholder="Ref, name, manufacturer, model, serial…"
                    className="h-9 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
                <div>
                  <FormLabel className="text-gray-700 dark:text-gray-300">Category</FormLabel>
                  <select
                    value={categoryFilter}
                    onChange={(e) => {
                      setCategoryFilter(e.target.value);
                      setPagination((p) => ({ ...p, page: 1 }));
                    }}
                    className={selectClass}
                  >
                    <option value="">All categories</option>
                    {ASSET_CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <FormLabel className="text-gray-700 dark:text-gray-300">Status</FormLabel>
                  <select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPagination((p) => ({ ...p, page: 1 }));
                    }}
                    className={selectClass}
                    disabled={urlFilter === 'underRepair'}
                  >
                    <option value="">All statuses</option>
                    {ASSET_STATUSES.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <FormLabel className="text-gray-700 dark:text-gray-300">Assigned to</FormLabel>
                  <select
                    value={assignedFilter}
                    onChange={(e) => {
                      setAssignedFilter(e.target.value);
                      setPagination((p) => ({ ...p, page: 1 }));
                    }}
                    className={selectClass}
                  >
                    <option value="">All</option>
                    <option value="__none__">Unassigned</option>
                    {tenantUsers
                      .filter((u) => ['tenant_admin', 'tenant_user'].includes(u.role))
                      .map((u) => (
                        <option key={u.id} value={u.id}>
                          {[u.firstName ?? u.first_name, u.lastName ?? u.last_name].filter(Boolean).join(' ').trim() || u.email}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          <Card className="overflow-hidden bg-white dark:bg-gray-800">
            {loading ? (
              <div className="p-8 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--ds-accent-primary)]" />
                <p className="mt-2 text-body text-[var(--ds-text-muted)]">Loading assets…</p>
              </div>
            ) : assets.length === 0 ? (
              <div className="p-8 text-center text-body text-[var(--ds-text-muted)]">
                <Package className="h-12 w-12 mx-auto mb-3 opacity-40" />
                <p>No assets found</p>
                <Button variant="primary" onClick={() => setShowCreateSlideOver(true)} className="mt-4">
                  Add your first asset
                </Button>
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Asset</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Assigned</TableHead>
                      <TableHead>Calibration due</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {assets.map((asset) => (
                      <TableRow key={asset.id}>
                        <TableCell>
                          <div className="flex items-center">
                            <div className="h-10 w-10 rounded-lg bg-[rgba(59,130,246,0.15)] flex items-center justify-center text-[var(--ds-accent-primary)] font-mono text-xs font-bold">
                              {asset.assetRef?.replace('AST-', '')?.slice(0, 4) || '—'}
                            </div>
                            <div className="ml-3">
                              <div className="text-sm font-medium text-[var(--ds-text-primary)]">{asset.name}</div>
                              <div className="text-xs text-[var(--ds-text-muted)] font-mono">{asset.assetRef}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{labelFor(ASSET_CATEGORIES, asset.category)}</TableCell>
                        <TableCell>
                          <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${statusBadgeClass(asset.status)}`}>
                            {labelFor(ASSET_STATUSES, asset.status)}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm">
                          {asset.assignedUserName || asset.assignedVehicleReg || '—'}
                        </TableCell>
                        <TableCell className="text-sm whitespace-nowrap">
                          {formatAssetDate(asset.calibrationDueDate)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Tooltip content="View">
                              <Button
                                variant="icon"
                                size="sm"
                                onClick={() => navigate(`/tenant/assets/${asset.id}`)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </Tooltip>
                            <Tooltip content="Edit">
                              <Button
                                variant="icon"
                                size="sm"
                                onClick={() => {
                                  setSelectedAsset(asset);
                                  setShowEditSlideOver(true);
                                }}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {totalPages > 1 && (
                  <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Page {pagination.page} of {totalPages} ({pagination.total} assets)
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={pagination.page <= 1}
                        onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={pagination.page >= totalPages}
                        onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </Card>
        </div>

        <SlideOver
          open={showCreateSlideOver}
          onOpenChange={setShowCreateSlideOver}
          title="Add asset"
          width="w-full max-w-3xl"
        >
          <AssetForm onClose={() => setShowCreateSlideOver(false)} onSuccess={handleCreateSuccess} />
        </SlideOver>

        <SlideOver
          open={showEditSlideOver}
          onOpenChange={(open) => {
            setShowEditSlideOver(open);
            if (!open) setSelectedAsset(null);
          }}
          title="Edit asset"
          width="w-full max-w-3xl"
        >
          {selectedAsset && (
            <AssetForm
              asset={selectedAsset}
              onClose={() => {
                setShowEditSlideOver(false);
                setSelectedAsset(null);
              }}
              onSuccess={handleEditSuccess}
            />
          )}
        </SlideOver>
      </Layout>
    </TooltipProvider>
  );
};

export default AssetsList;
